import { ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

interface Breadcrumb {
  label: string;
  path?: string;
}

interface PageHeaderProps {
  title: string;
  description?: string;
  breadcrumbs?: Breadcrumb[];
  actions?: ReactNode;
  /** @deprecated icon prop is no longer rendered. Kept for backward compatibility. */
  icon?: ReactNode;
}

export function PageHeader({
  title,
  description,
  breadcrumbs,
  actions,
}: PageHeaderProps) {
  const navigate = useNavigate();

  return (
    <div className="border-b border-border/50 bg-background">
      <div className="container mx-auto px-4 py-5 md:py-7">
        {/* Breadcrumbs - hidden on mobile */}
        {breadcrumbs && breadcrumbs.length > 0 && (
          <div className="hidden md:flex items-center gap-2 text-xs text-muted-foreground mb-3 uppercase tracking-widest font-medium">
            {breadcrumbs.map((crumb, index) => (
              <div key={index} className="flex items-center gap-2">
                {crumb.path ? (
                  <Button
                    variant="link"
                    className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground uppercase tracking-widest font-medium"
                    onClick={() => navigate(crumb.path!)}
                  >
                    {crumb.label}
                  </Button>
                ) : (
                  <span>{crumb.label}</span>
                )}
                {index < breadcrumbs.length - 1 && <ChevronRight className="h-3 w-3" />}
              </div>
            ))}
          </div>
        )}

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight truncate">
              {title}
            </h1>
            {description && (
              <p className="text-sm md:text-base text-muted-foreground mt-1 line-clamp-2">
                {description}
              </p>
            )}
          </div>

          {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
        </div>
      </div>
    </div>
  );
}
